import React, { useState } from 'react';
import { Descriptions, Statistic, Segmented, Switch, Badge, Input, Button, Form, Card, Col, Row, Modal, Collapse, Table } from 'antd';
import { Slider } from 'antd';
import { Typography } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Tag } from 'antd';
import SetupWizard from './SetupWizard'; // Adjust the path as needed


const { Title } = Typography;
const { Text } = Typography;
const { Panel } = Collapse;

const PowerConsumptionCalculator = () => {

    const [isModalVisible, setIsModalVisible] = useState(false);

    const [wizardVisible, setWizardVisible] = useState(false);

    const showWizardModal = () => setWizardVisible(true);
    const hideWizardModal = () => setWizardVisible(false);

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleOk = () => {
        setIsModalVisible(false);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const [currencySymbol, setCurrencySymbol] = useState('$');

    const CurrencyToggle = ({ currencySymbol, setCurrencySymbol }) => {
        return (
            <Segmented
                options={[
                    { label: '$', value: '$' },
                    { label: '€', value: '€' }
                ]}
                value={currencySymbol}
                onChange={value => setCurrencySymbol(value)}
            />
        );
    };

    const [detailedResults, setDetailedResults] = useState({});
    const [aggregatedResults, setAggregatedResults] = useState({
        maximumPower: 0,
        optimizedPower: 0,
        superOptimizedPower: 0
    });

    const [pricePerKw, setPricePerKw] = useState(0.12); // Sample price per kWh
    
    const updatePricePerKw = (newPrice) => {
        setPricePerKw(newPrice);
        setFormData(prevFormData => ({
            ...prevFormData,
            pricePerKw: newPrice
        }));
    };

    const displayTypes = {
        indoor: { maxPower: 0.138, size: '49"', nitts: '350cd', model: 'QB49R' },
        semiOutdoor: { maxPower: 0.35, size: '46"', nitts: '4000cd', model: 'OM46N' },
        outdoor: { maxPower: 0.4, size: '46"', nitts: '4000cd', model: 'OH46F' },
        eInk: { maxPower: 0.01, size: '24"', nitts: '0cd', model: 'Philips' }
    };
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const types = ['indoor', 'semiOutdoor', 'outdoor', 'eInk'];

    const greenShades = ['#09d3090a','#9cfd9c45', '#B3F0B3', '#99EB99']; // Array of light green shades

    // Helper function to convert minutes to HH:MM format
    const formatTime = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    };

    // Helper function to convert HH:MM to minutes
    const timeToMinutes = (time) => {
        const [hours, minutes] = time.split(':').map(Number);
        return (hours * 60) + minutes;
    };

    const formatNumber = (number, style = 'decimal', currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: style,
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(number);
    };

    const initialScheduleState = () => {
        const schedules = {};
        types.forEach(type => {
            schedules[type] = {};
            days.forEach(day => {
                schedules[type][day.toLowerCase()] = {
                    time: '00:00-00:00',  // Default time range when disabled
                    enabled: false  // Initially disabled
                };
            });
        });
        return schedules;
    };
    

    const [formData, setFormData] = useState({
        counts: { indoor: 1, semiOutdoor: 0, outdoor: 0, eInk: 0 },
        brightnessLevels: { indoor: 80, semiOutdoor: 70, outdoor: 90, eInk: 50 },
        schedules: initialScheduleState(),
        pricePerKw: 0.12
    });    

    const handleInputChange = (type, field, day, value, isEnabled) => {
        if (!type || !field) {
            console.error('Type or field is undefined');
            return;
        }
    
        // Check if 'day' is provided and is a string
        if (day && typeof day === 'string') {
            setFormData(prev => {
                return ({
                    ...prev,
                    [field]: {
                        ...prev[field],
                        [type]: {
                            ...prev[field][type],
                            [day.toLowerCase()]: {
                                time: value,
                                enabled: isEnabled
                            }
                        }
                    }
                })
            });
        } else {
            // Handle cases where `day` is undefined
            setFormData(prev => ({
                ...prev,
                [field]: {
                    ...prev[field],
                    [type]: value  // Directly set the value without day indexing
                }
            }));
        }

    };

    const toggleScheduleEnabled = (type, day, isEnabled) => {
        const defaultTime = isEnabled ? '08:00-22:00' : '00:00-00:00';
        handleInputChange(type, 'schedules', day, defaultTime, isEnabled);
    };    
    
    const gatherScheduleData = () => {
        let scheduleData = {};
        types.forEach(type => {
            scheduleData[type] = {};
            days.forEach(day => {
                scheduleData[type][day.toLowerCase()] = formData.schedules[type][day.toLowerCase()] || '';
            });
        });
        return scheduleData;
    };    

    function calculateResults() {
            hideWizardModal();
            const schedules = gatherScheduleData();
            let localAggregatedResults = {
                maximumPower: 0,
                optimizedPower: 0,
                superOptimizedPower: 0
            };
            
            let localDetailedResults = {};
            
            Object.keys(displayTypes).forEach(type => {
                const count = parseInt(formData.counts[type], 10);
                const brightness = parseInt(formData.brightnessLevels[type], 10);
                const maxPower = displayTypes[type].maxPower;
                const adjustedPower = calculatePower(type, brightness);
            
                const results = {
                    maximumPower: count * maxPower * 24 * 7 * 52, // Max power 24/7
                    optimizedPower: count * adjustedPower * 24 * 7 * 52, // Optimized power 24/7
                    superOptimizedPower: 0 // Initialized for later calculation
                };
            
                const weeklyHours = calculateWeeklyHours(schedules, type);
                results.superOptimizedPower = count * adjustedPower * weeklyHours * 52;
            
                localDetailedResults[type] = results;
            
                localAggregatedResults.maximumPower += results.maximumPower;
                localAggregatedResults.optimizedPower += results.optimizedPower;
                localAggregatedResults.superOptimizedPower += results.superOptimizedPower;
            });
        
            setDetailedResults(localDetailedResults);
            setAggregatedResults(localAggregatedResults);
            showModal();
            //displayResults(detailedResults, aggregatedResults);
    }
        
    function calculatePower(displayType, brightness) {
        return displayTypes[displayType].maxPower * (brightness / 100);
    }
        
    function calculateWeeklyHours(schedules, displayType) {
        let totalHours = 0;
        Object.values(schedules[displayType]).forEach(schedule => {
            if (schedule.enabled) {
                const timeRange = typeof schedule === 'string' ? schedule : (schedule.time || "00:00-00:00");
                const times = timeRange.split('-');
                const start = parseInt(times[0].split(':')[0], 10);
                const end = parseInt(times[1].split(':')[0], 10);
                totalHours += (end > start) ? (end - start) : (24 - start + end); // Handling overnight schedule
            } else {
                totalHours += 0;
            }
        });
        return totalHours;
    }
                

    const prepareTableData = (displayType, dataResults, pricePerKw) => {
        if(!dataResults) return [];

        const data = [];
        const { maximumPower, optimizedPower, superOptimizedPower } = dataResults;
        const numberOfWeeksPerYear = 1;
        /*
            All data below represents power consumption per week
        */

        // Assuming you want to show these three types in the table
        data.push({
            key: `${displayType}-max`,
            powerType: `Always On Mode (${displayType.charAt(0).toUpperCase() + displayType.slice(1)})`,
            powerTypeHint: 'Displays are set on 100% brightness and are running 24/7',
            powerConsumption: maximumPower,
            averageCosts: maximumPower * numberOfWeeksPerYear * pricePerKw,
            difference: null,
        });
        data.push({
            key: `${displayType}-opt`,
            powerType: `Adjusted Brightness Mode (${displayType.charAt(0).toUpperCase() + displayType.slice(1)})`,
            powerTypeHint: 'Displays are set on defined brightness level and are running 24/7',
            powerConsumption: optimizedPower,
            averageCosts: optimizedPower * numberOfWeeksPerYear * pricePerKw,
            difference: (maximumPower * numberOfWeeksPerYear * pricePerKw)-(optimizedPower * numberOfWeeksPerYear * pricePerKw),
        });
        data.push({
            key: `${displayType}-sup`,
            powerType: `Adjusted Brightness + Schedule Mode (${displayType.charAt(0).toUpperCase() + displayType.slice(1)})`,
            powerTypeHint: 'Displays are set on defined brightness level and are running according to time schedule',
            powerConsumption: superOptimizedPower,
            averageCosts: superOptimizedPower * numberOfWeeksPerYear * pricePerKw,
            difference: (maximumPower * numberOfWeeksPerYear * pricePerKw)-(superOptimizedPower * numberOfWeeksPerYear * pricePerKw),
        });
        return data;
    };

    const columns = [
        {
            title: 'Power Type',
            dataIndex: 'powerType',
            key: 'powerType',
            render: (text, record) => (
                <div>
                    <div>{text}</div>
                    <div style={{ color: 'gray', fontSize: 'smaller' }}>{record.powerTypeHint}</div>
                </div>
            )
        },
        {
            title: 'Annual Power Consumption (kWh)',
            dataIndex: 'powerConsumption',
            key: 'powerConsumption',
            render: (text) => `${formatNumber(text)} kWh`,
        },
        {
            title: `Annual Average Costs (${currencySymbol})`,
            dataIndex: 'averageCosts',
            key: 'averageCosts',
            render: (text) => `${currencySymbol}${formatNumber(text)}`,  // Formatting to 2 decimal places
        },
        {
            title: `Annual Costs Savings (${currencySymbol})`,
            dataIndex: 'difference',
            key: 'difference',
            render: (text) => (text ? <Tag color="green">{`💸 ${currencySymbol}${formatNumber(text)}`}</Tag> : ""),  // Formatting to 2 decimal places
        }
    ];        

    const detailedTableData = {
        aggregated: prepareTableData('aggregated', aggregatedResults, pricePerKw),
        indoor: prepareTableData('indoor', detailedResults['indoor'], pricePerKw),
        semiOutdoor: prepareTableData('semiOutdoor', detailedResults['semiOutdoor'], pricePerKw),
        outdoor: prepareTableData('outdoor', detailedResults['outdoor'], pricePerKw),
        eInk: prepareTableData('eInk', detailedResults['eInk'], pricePerKw),
    };

    const panelHeaderGenerator = (type) => {
        const numDisplays = formData.counts[type] ? formData.counts[type] : "--";
        const brightnessLevel = formData.brightnessLevels[type];
        const scheduleSet = Object.values(formData.schedules[type]).some(schedule => schedule.time !== '00:00-00:00');
    
        return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span>{`${type.charAt(0).toUpperCase() + type.slice(1)} Displays`}</span>
                <div>
                    # of displays:&nbsp;
                    <Badge count={numDisplays} style={{ backgroundColor: 'grey', marginRight: '8px' }} />
                    Brightness: {brightnessLevel}%
                    <Badge
                        status={scheduleSet ? "success" : "error"}
                        text={scheduleSet ? "Schedule Set" : "!! Schedule Not Set (always off)"}
                        style={{ marginLeft: '10px' }}
                    />
                </div>
            </div>
        );
    }

    const displayStatistics = Object.entries(formData.counts)
        .filter(([type, count]) => count > 0)  // Filter to only include types with more than 0 displays
        .map(([type, count]) => ({
            type,
            count,
            brightness: formData.brightnessLevels[type]
    }));

    return (
        <>
            <Card className='semi-transparent'>
                <Row>
                    <Col span={24}>
                        <img src={`${process.env.PUBLIC_URL}/signageOS-full-white.png`} alt="signageOS Logo" style={{ height: '60px', verticalAlign: 'middle' }} />
                        <Title level={'1'} style={{fontSize: '3em', marginTop: '0'}}>
                            <Text type='secondary' style={{fontSize: '0.8em', paddingBottom: '1em'}}>Digital Signage</Text>
                            <br/>
                            Power Consumption Calculator</Title>
                    </Col>
                </Row>
                <Row style={{padding: '30px'}}>
                    <Col span={24}>
                        <Button size='large' type="primary" onClick={showWizardModal} className='custom-primary-btn'>Start Here 🚀</Button>
                    </Col>
                    <Col span={24} style={{paddingTop: '12px'}}>
                        <Text type='secondary'>Or modify the number of displays and their settings below 👇</Text>
                    </Col>
                </Row>
                <Form layout="vertical">
                    <Collapse>
                    {types.map(type => {
                        
                        const panelHeader = panelHeaderGenerator(type);
                        return (
                            <Panel header={panelHeader} key={type} style={{ background: "white", borderRadius: "5px" }}>
                            <Row gutter={16} key={type}>
                                <Col span={12}>
                                    <Form.Item style={{textTransform: 'uppercase'}} label={`Display Count`}>
                                        <Input
                                            type="number"
                                            value={formData.counts[type]}
                                            onChange={e => handleInputChange(type, 'counts', undefined, parseInt(e.target.value) || 0)}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16} key={type}>
                                <Col span={24} style={{textAlign: 'left'}}>
                                    <Text style={{textAlign: 'left', fontWeight: 'bold', fontSize: '1.5em'}}>
                                        Adjust Brightness and Power Schedule to see your savings.
                                    </Text>
                                </Col>
                                <Col span={12}>
                                    <Form.Item style={{textTransform: 'uppercase'}} label={`Brightness Level (%)`}>
                                        <Row>
                                            <Col span={20}>
                                                <Slider
                                                    min={0}
                                                    max={100}
                                                    onChange={(value) => handleInputChange(type, 'brightnessLevels', undefined, value)}
                                                    value={typeof formData.brightnessLevels[type] === 'number' ? formData.brightnessLevels[type] : 0}
                                                    tooltipVisible={false}
                                                />
                                            </Col>
                                            <Col span={4} style={{ paddingLeft: '10px', textAlign: 'center' }}>
                                                {formData.brightnessLevels[type]}%
                                            </Col>
                                        </Row>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/image.png`}
                                            alt="Placeholder"
                                            style={{
                                                width: '70%',
                                                border: '8px solid black',
                                                margin: '14px 0',
                                                float: 'left',
                                                filter: `brightness(${formData.brightnessLevels[type] / 100})`
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12} style={{textAlign: 'left'}}>
                                <Text style={{textTransform: 'uppercase'}}>Display On/off schedule</Text>
                                {days.map(day => (
                                    <Form.Item
                                    label={`${day} Schedule`}
                                    key={day}
                                >
                                    <Switch
                                        checked={formData.schedules[type][day.toLowerCase()].enabled}
                                        onChange={checked => toggleScheduleEnabled(type, day, checked)}
                                        style={{ marginBottom: '14px', marginRight: '14px' }}
                                        checkedChildren="Display on"
                                        unCheckedChildren="Display off"
                                    />

                                    <Slider
                                        range
                                        disabled={!formData.schedules[type][day.toLowerCase()].enabled}
                                        min={0}
                                        max={1440}
                                        step={1}
                                        value={[
                                            timeToMinutes(formData.schedules[type][day.toLowerCase()].time.split('-')[0]),
                                            timeToMinutes(formData.schedules[type][day.toLowerCase()].time.split('-')[1])
                                        ]}
                                        onChange={(value) => {
                                            const newTimeRange = `${formatTime(value[0])}-${formatTime(value[1])}`;
                                            handleInputChange(type, 'schedules', day, newTimeRange, true);
                                        }}
                                        tipFormatter={value => formatTime(value)}
                                        style={{ width: '200px', display: 'inline-block' }}
                                    />

                                    <Input
                                        disabled={!formData.schedules[type][day.toLowerCase()].enabled}
                                        value={formData.schedules[type][day.toLowerCase()].time}
                                        onChange={e => handleInputChange(type, 'schedules', day, e.target.value, true)}
                                        style={{ width: 'calc(100% - 360px)', marginLeft: '14px' }}
                                    />

                                </Form.Item>                                
                                ))}
                                </Col>
                            </Row>
                        </Panel>
                        );
                    })}
                    </Collapse>
                    <Row style={{marginTop: "16px"}}>
                        <Col>
                            <Form.Item label={`Price per kWh (${currencySymbol})`}>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={formData.pricePerKw}
                                    onChange={e => setFormData({...formData, pricePerKw: parseFloat(e.target.value)})}
                                />
                            </Form.Item>
                        </Col>
                        <Col>
                            <Form.Item label={`Toggle Currency`}>
                                <CurrencyToggle currencySymbol={currencySymbol} setCurrencySymbol={setCurrencySymbol} />
                            </Form.Item>
                        </Col>
                        <Col>
                            <Form.Item label={` `}>
                                <Button type="default" onClick={calculateResults}>Calculate</Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
            
            <Modal width='80vw' title="Power Consumption Results" visible={isModalVisible} onOk={handleOk} onCancel={handleCancel}
                footer={[
                    <Button key="back" onClick={handleCancel}>
                    Return
                    </Button>,
                    <Button key="submit" type="primary" onClick={() => window.print()}>
                    Print
                    </Button>,
                    <Button
                    key="link"
                    href="https://www.linkedin.com/sharing/share-offsite/?url=https://demo.signageos.io/power-consumption-calculator/index.html"
                    type="primary"
                    onClick={handleOk}
                    >
                    Share on LinkedIn
                    </Button>,
                ]}
              >
                <div className="printable">
                    {displayStatistics.length > 0 && (
                        <div>
                            <Title level={5}>Configured Displays</Title>
                            <Row gutter={16} style={{marginBottom: '12px'}}>
                                    {displayStatistics.map(({ type, count, brightness }, index) => (
                                    <Col span={12} style={{marginBottom: "8px"}}>
                                    <Card style={{ backgroundColor: greenShades[index % greenShades.length] }}>
                                        <Col key={type} span={24}>
                                            <Descriptions layout="horizontal" bordered="true" size="small" column={1} title={`${type.charAt(0).toUpperCase() + type.slice(1)} Displays`}>
                                                <Descriptions.Item label="Specs">{`${displayTypes[type].size}; ${displayTypes[type].nitts}`}</Descriptions.Item>
                                                <Descriptions.Item label="Number Of Displays">{count}</Descriptions.Item>
                                                <Descriptions.Item label="Brightness">{`${brightness}%`}</Descriptions.Item>
                                                <Descriptions.Item label="On/Off Schedule"><Badge
                                                    status={Object.values(formData.schedules[type]).some(schedule => schedule.time !== '00:00-00:00') ? "success" : "error"}
                                                    text={Object.values(formData.schedules[type]).some(schedule => schedule.time !== '00:00-00:00') ? "Schedule Set" : "Not set"}
                                                    style={{ marginLeft: '10px' }}
                                                /></Descriptions.Item>
                                            </Descriptions>
                                        </Col>
                                    </Card>
                                                </Col>
                                    ))}
                            </Row>
                        </div>
                    )}

                    <Row>
                        <Col span={24}>
                        <Title level={5}>Aggregated Power Consumption Comparisson</Title>
                        <Text>Compare total energy consumption of all devices, based on applied savings options.</Text>
                            <Table columns={columns} dataSource={detailedTableData['aggregated']} pagination={false} />
                        </Col>
                        <Col span={24} style={{marginTop: '14px'}}>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={detailedTableData['aggregated']} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="powerType" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="powerConsumption" fill="#8884d8" name="Power Consumption (kWh)" />
                                    <Bar dataKey="averageCosts" fill="#82ca9d" name={`Average Costs (${currencySymbol})`} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Col>
                    </Row>
                </div>
                <h3>Detailed Consumption by Display Type</h3>
                <Collapse>
                    {Object.keys(detailedResults).map(type => {
                            const panelHeader = panelHeaderGenerator(type);
                            return (
                                <Panel header={panelHeader} key={type} style={{ background: "white", borderRadius: "5px" }}>
                                    <div>
                                        <Table columns={columns} dataSource={detailedTableData[type]} pagination={false} />
                                    </div>
                                </Panel>
                            )
                        }
                    )}
                </Collapse>
            </Modal>
            <SetupWizard
                visible={wizardVisible}
                closeModal={calculateResults}
                formData={formData}
                handleInputChange={handleInputChange}
                calculateResults={calculateResults}
                pricePerKw={pricePerKw}
                toggleScheduleEnabled={toggleScheduleEnabled}
                types={types}
                days={days}
                currencySymbol={currencySymbol}
                setCurrencySymbol={setCurrencySymbol}
                CurrencyToggle={CurrencyToggle}
                setPricePerKw={updatePricePerKw}
            />

        </>
    );
};

export default PowerConsumptionCalculator;